import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import CourierLoader from "@/components/CourierLoader";
import { Toaster } from "sonner";
import { AuthProvider, ProtectedRoute } from "@/context/AuthContext";
import ScrollToTop from "@/components/ScrollToTop";
import { RouteSeo } from "@/components/SEO";
import ProhibitedItems from "@/pages/ProhibitedItems";

import Home from "@/pages/Home";
import StaticPage from "@/pages/StaticPage";
import TrackPage from "@/pages/TrackPage";
import Features from "@/pages/Features";
import Pricing from "@/pages/Pricing";
import Domestic from "@/pages/Domestic";
import InternationalShipping from "@/pages/InternationalShipping";
import About from "@/pages/About";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import RefundPolicy from "@/pages/Refund";
import ShippingPolicy from "@/pages/ShippingPolicy";
import NotFound from "@/pages/NotFound";
import ApiStatus from "@/pages/ApiStatus";
import ForgotPassword from "@/pages/ForgotPassword";
import SetPassword from "@/pages/SetPassword";
import AdminLogin from "@/pages/AdminLogin";
import FranchiseLogin from "@/pages/FranchiseLogin";
import TrackShipment from "@/pages/TrackShipment";
import Developers from "@/pages/Developers";
import Faq from "@/pages/Faq";
import Contact from "@/pages/Contact";
import AuthPage from "@/pages/AuthPage";

import DashboardLayout from "@/components/DashboardLayout";
import Dashboard from "@/pages/app/Dashboard";
import CreateShipment from "@/pages/app/CreateShipment";
import RateCalculator from "@/pages/app/RateCalculator";
import Shipments from "@/pages/app/Shipments";
import BulkShipping from "@/pages/app/BulkShipping";
import Track from "@/pages/app/Track";
import Warehouses from "@/pages/app/Warehouses";
import Wallet from "@/pages/app/Wallet";
import Billing from "@/pages/app/Billing";
import Support from "@/pages/app/Support";
import Settings from "@/pages/app/Settings";
import Kyc from "@/pages/app/Kyc";
import NdrCenter from "@/pages/app/NdrCenter";
import Returns from "@/pages/app/Returns";
import CodDashboard from "@/pages/app/CodDashboard";
import WarehouseOps from "@/pages/app/WarehouseOps";
import Manifests from "@/pages/app/Manifests";
import Inventory from "@/pages/app/Inventory";
import Analytics from "@/pages/app/Analytics";
import Disputes from "@/pages/app/Disputes";

import AdminLayout from "@/components/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminCustomerDetail from "@/pages/admin/AdminCustomerDetail";
import AdminKyc from "@/pages/admin/AdminKyc";
import AdminProviders from "@/pages/admin/AdminProviders";
import AdminCouriers from "@/pages/admin/AdminCouriers";
import AdminPricing from "@/pages/admin/AdminPricing";
import AdminRateCards from "@/pages/admin/AdminRateCards";
import AdminShipments from "@/pages/admin/AdminShipments";
import AdminWarehouses from "@/pages/admin/AdminWarehouses";
import AdminManifests from "@/pages/admin/AdminManifests";
import AdminCod from "@/pages/admin/AdminCod";
import AdminWallets from "@/pages/admin/AdminWallets";
import AdminBilling from "@/pages/admin/AdminBilling";
import AdminLogs from "@/pages/admin/AdminLogs";
import AdminSupport from "@/pages/admin/AdminSupport";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminNdr from "@/pages/admin/AdminNdr";
import AdminNdrRules from "@/pages/admin/AdminNdrRules";
import AdminRtoGuard from "@/pages/admin/AdminRtoGuard";
import AdminRules from "@/pages/admin/AdminRules";
import AdminPerformance from "@/pages/admin/AdminPerformance";
import AdminDisputes from "@/pages/admin/AdminDisputes";
import AdminAi from "@/pages/admin/AdminAi";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminReturns from "@/pages/admin/AdminReturns";
import AdminTeam from "@/pages/admin/AdminTeam";
import AdminDirectCouriers from "@/pages/admin/AdminDirectCouriers";
import AdminAutomation from "@/pages/admin/AdminAutomation";
import AdminProfit from "@/pages/admin/AdminProfit";
import AdminInsights from "@/pages/admin/AdminInsights";
import AdminApiUsers from "@/pages/admin/AdminApiUsers";
import International from "@/pages/app/International";
import InternationalDetail from "@/pages/app/InternationalDetail";
import AdminInternational from "@/pages/admin/AdminInternational";
import Developer from "@/pages/app/Developer";
import Integrations from "@/pages/app/Integrations";
import ApiConsoleLogin from "@/pages/api-console/ApiConsoleLogin";
import ApiConsoleLayout from "@/components/ApiConsoleLayout";
import ApiConsoleDashboard from "@/pages/api-console/ApiConsoleDashboard";
import ApiConsoleKeys from "@/pages/api-console/ApiConsoleKeys";
import ApiConsoleDocs from "@/pages/api-console/ApiConsoleDocs";
import ApiConsoleWallet from "@/pages/api-console/ApiConsoleWallet";
import ApiConsoleShipments from "@/pages/api-console/ApiConsoleShipments";
import ApiConsoleReports from "@/pages/api-console/ApiConsoleReports";
import ApiConsoleLogs from "@/pages/api-console/ApiConsoleLogs";
import ApiConsoleWebhooks from "@/pages/api-console/ApiConsoleWebhooks";
import ApiConsoleSandbox from "@/pages/api-console/ApiConsoleSandbox";
import ApiConsoleSettings from "@/pages/api-console/ApiConsoleSettings";
import ApiConsolePlayground from "@/pages/api-console/ApiConsolePlayground";
import FranchiseLayout from "@/components/FranchiseLayout";
import FranchiseDashboard from "@/pages/franchise/FranchiseDashboard";
import FranchiseCustomers from "@/pages/franchise/FranchiseCustomers";
import FranchiseCustomerDetail from "@/pages/franchise/FranchiseCustomerDetail";
import FranchiseShipments from "@/pages/franchise/FranchiseShipments";
import FranchiseEarnings from "@/pages/franchise/FranchiseEarnings";
import FranchiseWallet from "@/pages/franchise/FranchiseWallet";
import FranchiseBank from "@/pages/franchise/FranchiseBank";
import FranchiseSettings from "@/pages/franchise/FranchiseSettings";
import AdminFranchises from "@/pages/admin/AdminFranchises";
import AdminFranchiseDetail from "@/pages/admin/AdminFranchiseDetail";
import AdminFranchiseWithdrawals from "@/pages/admin/AdminFranchiseWithdrawals";

const STATIC_ROUTES = [
  "business-shipping", "integrations", "blog", "cookie-policy", "grievance",
];

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      {STATIC_ROUTES.map((key) => (
        <Route key={key} path={`/${key}`} element={<StaticPage pageKey={key} />} />
      ))}
      <Route path="/features" element={<Features />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/domestic-shipping" element={<Domestic />} />
      <Route path="/international-shipping" element={<InternationalShipping />} />
      <Route path="/about" element={<About />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/shipping-policy" element={<ShippingPolicy />} />
      <Route path="/prohibited-items" element={<ProhibitedItems />} />
      <Route path="/track-shipment" element={<TrackShipment />} />
      <Route path="/developers" element={<Developers />} />
      <Route path="/status" element={<ApiStatus />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/faq" element={<Faq />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/track" element={<TrackPage />} />
      <Route path="/track/:awb" element={<TrackPage />} />
      <Route path="/login" element={<AuthPage mode="login" />} />
      <Route path="/signup" element={<AuthPage mode="register" />} />
      <Route path="/ai-admin" element={<AdminLogin />} />
      <Route path="/franchise-login" element={<FranchiseLogin />} />
      <Route path="/console/login" element={<ApiConsoleLogin />} />
      <Route path="/console" element={<ApiConsoleLayout />}>
        <Route index element={<ApiConsoleDashboard />} />
        <Route path="keys" element={<ApiConsoleKeys />} />
        <Route path="docs" element={<ApiConsoleDocs />} />
        <Route path="wallet" element={<ApiConsoleWallet />} />
        <Route path="shipments" element={<ApiConsoleShipments />} />
        <Route path="reports" element={<ApiConsoleReports />} />
        <Route path="logs" element={<ApiConsoleLogs />} />
        <Route path="webhooks" element={<ApiConsoleWebhooks />} />
        <Route path="sandbox" element={<ApiConsoleSandbox />} />
        <Route path="playground" element={<ApiConsolePlayground />} />
        <Route path="settings" element={<ApiConsoleSettings />} />
      </Route>

      <Route path="/app" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="create-shipment" element={<CreateShipment />} />
        <Route path="quick-book" element={<Navigate to="/app/create-shipment" replace />} />
        <Route path="rate-calculator" element={<RateCalculator />} />
        <Route path="international" element={<International />} />
        <Route path="international/:id" element={<InternationalDetail />} />
        <Route path="developer" element={<Developer />} />
        <Route path="integrations" element={<Integrations />} />
        <Route path="shipments" element={<Shipments />} />
        <Route path="bulk-shipping" element={<BulkShipping />} />
        <Route path="track" element={<Track />} />
        <Route path="warehouses" element={<Warehouses />} />
        <Route path="warehouse-ops" element={<WarehouseOps />} />
        <Route path="manifests" element={<Manifests />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="ndr" element={<NdrCenter />} />
        <Route path="returns" element={<Returns />} />
        <Route path="cod" element={<CodDashboard />} />
        <Route path="disputes" element={<Disputes />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="wallet" element={<Wallet />} />
        <Route path="billing" element={<Billing />} />
        <Route path="support" element={<Support />} />
        <Route path="settings" element={<Settings />} />
        <Route path="kyc" element={<Kyc />} />
      </Route>

      <Route path="/franchise" element={<ProtectedRoute><FranchiseLayout /></ProtectedRoute>}>
        <Route index element={<FranchiseDashboard />} />
        <Route path="customers" element={<FranchiseCustomers />} />
        <Route path="customers/:id" element={<FranchiseCustomerDetail />} />
        <Route path="shipments" element={<FranchiseShipments />} />
        <Route path="earnings" element={<FranchiseEarnings />} />
        <Route path="wallet" element={<FranchiseWallet />} />
        <Route path="bank" element={<FranchiseBank />} />
        <Route path="settings" element={<FranchiseSettings />} />
      </Route>

      <Route path="/admin" element={<ProtectedRoute admin><AdminLayout /></ProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="insights" element={<AdminInsights />} />
        <Route path="profit" element={<AdminProfit />} />
        <Route path="team" element={<AdminTeam />} />
        <Route path="direct-couriers" element={<AdminDirectCouriers />} />
        <Route path="automation" element={<AdminAutomation />} />
        <Route path="ai" element={<AdminAi />} />
        <Route path="providers" element={<AdminProviders />} />
        <Route path="couriers" element={<AdminCouriers />} />
        <Route path="performance" element={<AdminPerformance />} />
        <Route path="rules" element={<AdminRules />} />
        <Route path="ndr" element={<AdminNdr />} />
        <Route path="ndr-rules" element={<AdminNdrRules />} />
        <Route path="rto-guard" element={<AdminRtoGuard />} />
        <Route path="returns" element={<AdminReturns />} />
        <Route path="disputes" element={<AdminDisputes />} />
        <Route path="customers" element={<AdminCustomers />} />
        <Route path="customers/:id" element={<AdminCustomerDetail />} />
        <Route path="api-users" element={<AdminApiUsers />} />
        <Route path="franchises" element={<AdminFranchises />} />
        <Route path="franchises/:id" element={<AdminFranchiseDetail />} />
        <Route path="franchise-withdrawals" element={<AdminFranchiseWithdrawals />} />
        <Route path="kyc" element={<AdminKyc />} />
        <Route path="international" element={<AdminInternational />} />
        <Route path="wallets" element={<AdminWallets />} />
        <Route path="support" element={<AdminSupport />} />
        <Route path="pricing" element={<AdminPricing />} />
        <Route path="rate-cards" element={<AdminRateCards />} />
        <Route path="shipments" element={<AdminShipments />} />
        <Route path="warehouses" element={<AdminWarehouses />} />
        <Route path="manifests" element={<AdminManifests />} />
        <Route path="cod" element={<AdminCod />} />
        <Route path="billing" element={<AdminBilling />} />
        <Route path="logs" element={<AdminLogs />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <RouteSeo />
          <AppRouter />
          <CourierLoader />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
